import { Rule } from 'antd/es/form';

const regex = {
	character: /^[a-zA-Z ]*$/,
	number: /^\d+$/,
	email:
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
	rtrw: /^(?!000)[0-9]{3}$/,
	phone: /^(0)[0-9-]{6,13}$/,
	kitas: /^[a-zA-Z0-9]{11}$/,
	passport: /^[a-zA-Z0-9]{7,9}$/,
};

const required = (message?: string) => {
	return {
		required: true,
		message: message ?? 'Wajib diisi',
	} as Rule;
};

const array = (message?: string) => {
	return {
		validator(_, value) {
			if (!value || !value.length)
				return Promise.reject(message ?? 'Wajib diisi');
			return Promise.resolve();
		},
	} as Rule;
};

const charOnly = (message?: string) => {
	return {
		pattern: regex.character,
		message: message ?? 'Input mengandung non-karakter',
	} as Rule;
};

const strNumber = (message?: string) => {
	return {
		pattern: regex.number,
		message: message ?? 'Nomor harus angka',
	} as Rule;
};

const phoneNumber = (message?: string) => {
	return {
		validator(_, value) {
			// Check if phone number starts with "00000" (not allowed)
			if (value && String(value).startsWith('00000')) {
				return Promise.reject(
					new Error('Nomor telepon tidak boleh diawali dengan 00000'),
				);
			}

			// Check phone number pattern
			if (!regex.phone.test(String(value))) {
				return Promise.reject(
					new Error(message ?? 'Format nomor telepon tidak valid'),
				);
			}

			return Promise.resolve();
		},
	} as Rule;
};

const strRtRw = (message?: string) => {
	return {
		pattern: regex.rtrw,
		message: message ?? 'Nomor RT/RW tidak valid',
	} as Rule;
};

const email = (message?: string) => {
	return {
		pattern: regex.email,
		message: message ?? 'Email tidak valid',
	} as Rule;
};

const kitas = (message?: string) => {
	return {
		pattern: regex.kitas,
		message: message ?? 'Kitas tidak valid',
	} as Rule;
};

const passport = (message?: string) => {
	return {
		pattern: regex.passport,
		message: message ?? 'Passport tidak valid',
	} as Rule;
};

const fileRequired = (message?: string) => {
	return {
		validator(_: unknown, value: { url?: string } | null) {
			if (!value?.url) return Promise.reject(message ?? 'Dokumen wajib diupload');
			return Promise.resolve();
		},
	} as Rule;
};

const rule = {
	required,
	array,
	charOnly,
	strNumber,
	strRtRw,
	phoneNumber,
	email,
	kitas,
	passport,
	fileRequired,
};

export default rule;

export const rules = {
	npwp: [
		required('NPWP harus diisi'),
		{
			validator(_, value) {
				const length = String(value).length;
				if (length === 16 || !value) {
					// Check if NPWP starts with "00000" (not allowed)
					if (value && String(value).startsWith('00000')) {
						return Promise.reject(
							new Error('NPWP tidak boleh diawali dengan 00000'),
						);
					}
					return Promise.resolve();
				}

				return Promise.reject(new Error('NPWP harus 16 digit'));
			},
		},
	] as Rule[],
	rt: [
		required('RT harus diisi'),
		strNumber('RT harus angka'),
		strRtRw('RT tidak boleh 000'),
		{
			max: 3,
			min: 3,
			message: 'Input RT wajib 3 digit',
		},
	] as Rule[],
	rw: [
		required('RW harus diisi'),
		strNumber('RW harus angka'),
		strRtRw('RW tidak boleh 000'),
		{
			max: 3,
			min: 3,
			message: 'Input RW wajib 3 digit',
		},
	] as Rule[],
	ktp: [
		required('Input KTP harus diisi'),
		strNumber('KTP harus angka'),
		{
			validator(_, value) {
				const length = String(value).length;
				if (length === 16 || !value) {
					// Check if KTP starts with "00000" or "000000" (not allowed)
					if (
						value &&
						(String(value).startsWith('00000') ||
							String(value).startsWith('000000'))
					) {
						return Promise.reject(
							new Error('KTP tidak boleh diawali dengan 00000 atau 000000'),
						);
					}
					return Promise.resolve();
				}

				return Promise.reject(new Error('Input KTP wajib 16 digit'));
			},
		},
	] as Rule[],
	kitas: [
		required('Kitas harus diisi'),
		kitas('Kitas harus diisi dengan 11 alfanumerik'),
		{
			validator(_, value) {
				// Check if Kitas starts with "00000" or "000000" (not allowed)
				if (
					value &&
					(String(value).startsWith('00000') ||
						String(value).startsWith('000000'))
				) {
					return Promise.reject(
						new Error('Kitas tidak boleh diawali dengan 00000 atau 000000'),
					);
				}
				return Promise.resolve();
			},
		},
	] as Rule[],
	passport: [
		required('Passport harus diisi'),
		passport('Passport harus diisi dengan 7-9 alfanumerik'),
		{
			validator(_, value) {
				// Check if Passport starts with "00000" or "000000" (not allowed)
				if (
					value &&
					(String(value).startsWith('00000') ||
						String(value).startsWith('000000'))
				) {
					return Promise.reject(
						new Error('Passport tidak boleh diawali dengan 00000 atau 000000'),
					);
				}
				return Promise.resolve();
			},
		},
	] as Rule[],
};
